import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Card, Form, Spinner, Table } from "react-bootstrap";
import { getAuditLogs } from "../api/api";

const actionLabels = {
  reset_password: "Reset mot de passe",
  delete: "Suppression",
  approve_family: "Approbation famille",
  reject_family: "Refus famille",
  login_blocked: "Login bloque",
};

function explainLog(log) {
  const details = log.details || {};
  if (log.action === "reset_password") return `Mot de passe reinitialise pour ${log.target_type} #${log.target_id}.`;
  if (log.action === "delete") return `Suppression logique de ${log.target_type} #${log.target_id}.`;
  if (log.action === "approve_family") {
    return details.applied
      ? `Demande famille approuvee et appliquee sur ${details.target_type || "famille"} #${details.target_id || "-"}.`
      : "Demande famille approuvee sans modification automatique.";
  }
  if (log.action === "reject_family") return "Demande famille refusee avec note admin.";
  if (log.action === "login_blocked") return `Trop de tentatives login depuis ${details.ip || "IP inconnue"}.`;
  return "Action administrative enregistree pour audit.";
}

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getAuditLogs();
      const payload = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setLogs(payload);
    } catch (e) {
      setError("Impossible de charger les logs admin.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return logs;
    return logs.filter((log) => {
      const blob = [
        log.action,
        actionLabels[log.action],
        log.target_type,
        log.target_id,
        log.actor_name,
        log.actor_matricule,
        explainLog(log),
      ]
        .join(" ")
        .toLowerCase();
      return blob.includes(term);
    });
  }, [logs, search]);

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
        <div>
          <span className="profile-eyebrow">Audit</span>
          <h4 className="mb-1">Logs admin</h4>
          <p className="text-muted mb-0">
            Tableau de controle des actions sensibles avec le pourquoi de chaque operation.
          </p>
        </div>
        <Form.Control
          style={{ maxWidth: 320 }}
          placeholder="Rechercher action, cible, admin..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm border-0">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table responsive hover className="align-middle">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Action</th>
                  <th>Acteur</th>
                  <th>Cible</th>
                  <th>Pourquoi</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      Aucun log trouve.
                    </td>
                  </tr>
                ) : (
                  filtered.map((log) => (
                    <tr key={log.id}>
                      <td>{log.created_at ? new Date(log.created_at).toLocaleString() : "-"}</td>
                      <td>
                        <Badge bg="success">{actionLabels[log.action] || log.action}</Badge>
                      </td>
                      <td>
                        <strong>{log.actor_name || "Systeme"}</strong>
                        <div className="text-muted small">{log.actor_matricule || "-"}</div>
                      </td>
                      <td>
                        <strong>{log.target_type || "-"}</strong>
                        <div className="text-muted small">#{log.target_id || "-"}</div>
                      </td>
                      <td style={{ minWidth: 280 }}>{explainLog(log)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
